import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"

import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { Results } from "@/modules/jobAds/client/components/Results/Results"
import { ResultsHeader } from "@/modules/jobAds/client/components/ResultsHeader/ResultsHeader"
import { ResultsLoading } from "@/modules/jobAds/client/components/ResultsLoading/ResultsLoading"
import { SavedSearchesButton } from "@/modules/jobAds/client/components/SavedSearchesButton/SavedSearchesButton"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { useSavedSearchesButton } from "@/modules/jobAds/client/hooks/useSavedSearchesButton"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

const DynamicEmptyState = dynamic(() =>
  import("@/modules/layouts/components/State/EmptyState/EmptyState").then(component => component.EmptyState)
)
const DynamicErrorState = dynamic(() =>
  import("@/modules/layouts/components/State/ErrorState/ErrorState").then(component => component.ErrorState)
)

export const ResultsWrapper: FunctionComponent<{ isSavedSearchOnMobileVisible?: boolean }> = ({
  isSavedSearchOnMobileVisible = false,
}) => {
  const { t } = useTranslation(["job_ads_fo"])
  const { dataJobAds, isLoadingJobAds, isErrorJobAds } = useJobAdsFromAlgolia()
  const { dataCurrentUser } = useCurrentUserFetch()
  const isLogged = dataCurrentUser?.isLogged
  const { isSearchEmpty } = useSavedSearchesButton()
  const isSavedSearchesButtonDisplayed = isLogged && !isSearchEmpty
  const { isJobAlertFromSavedSearchFeatureEnabled } = usePageContext()

  if (isLoadingJobAds) {
    return <ResultsLoading />
  }

  if (isErrorJobAds) {
    return <DynamicErrorState />
  }

  const jobAds = dataJobAds?.jobAds

  if (!jobAds || jobAds.length === 0) {
    return <DynamicEmptyState />
  }

  return (
    <>
      <ResultsHeader jobAdsCount={dataJobAds.totalSize} />
      <Results jobAds={jobAds} />
      {isSavedSearchesButtonDisplayed && (
        <SavedSearchesButton
          alreadySavedLabel={t("job_ads_fo.client.search.job_alert.already_saved_button")}
          errorMessage={t("job_ads_fo.client.search.saved_searches.notification.error.v2.content")}
          errorTitle={t("job_ads_fo.client.search.saved_searches.notification.error.v2.title")}
          isJobAlertFromSavedSearchFeatureEnabled={isJobAlertFromSavedSearchFeatureEnabled}
          isSavedSearchOnMobileVisible={isSavedSearchOnMobileVisible}
          saveLabel={t("job_ads_fo.client.search.job_alert.save_button")}
        />
      )}
    </>
  )
}
